import { ContentLayout, Icon, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logUserClick } from 'src/analytics/KatalLogger';
import { NAV_SECTION_GROUPS } from 'src/constants/business-group-constants';
import { DataClassification } from 'src/models/AppContextModels';
import { Status } from 'src/models/AuthContextModels';
import { RootState } from 'src/store/store';
import { compareNullableNumbers } from 'src/utils/generic-utilities';
import { navigateWithEmbedParams } from 'src/utils/navigation-utils';
import { selectActiveBusinessGroups } from '../business-group/businessGroupSelectors';

export const useDataClassificationShortDescShortDescFromPath = () => {
  const location = useLocation();
  const pathParts = location.pathname.split('/');

  // Decode the URL-encoded string
  const businessGroupShortDesc = decodeURIComponent(pathParts[1]);
  return businessGroupShortDesc;
};

/**
 * Groups data classifications into navigation sections based on NAV_SECTION_GROUPS.
 * Each section contains classifications that match the short descriptions defined in the section.
 * Classifications not matching any section are placed in an 'unassigned' group before the last section (DMM).
 * Within each section, items are sorted by the order defined in NAV_SECTION_GROUPS.
 */
export const groupClassificationsIntoSections = (dataClassifications: DataClassification[]): DataClassification[][] => {
  const uppercaseGroups = NAV_SECTION_GROUPS.map((section) => section.map((code) => code.toUpperCase()));

  // Track which classifications have been assigned to a section
  const assignedIds = new Set<number | null>();

  const sections: DataClassification[][] = uppercaseGroups.map((sectionCodes) => {
    const sectionItems = dataClassifications.filter((dc) => {
      const shortDesc = dc.data_classification_short_description?.toUpperCase();
      return sectionCodes.includes(shortDesc);
    });

    // Sort items within section by the order defined in NAV_SECTION_GROUPS
    sectionItems.sort((a, b) => {
      const aIndex = sectionCodes.indexOf(a.data_classification_short_description?.toUpperCase());
      const bIndex = sectionCodes.indexOf(b.data_classification_short_description?.toUpperCase());
      return aIndex - bIndex;
    });

    sectionItems.forEach((item) => assignedIds.add(item.data_classification_id));
    return sectionItems;
  });

  // Collect unassigned classifications
  const unassigned = dataClassifications.filter((dc) => !assignedIds.has(dc.data_classification_id));

  if (unassigned.length > 0) {
    // Insert unassigned items before the last section (DMM)
    const insertIndex = sections.length > 0 ? sections.length - 1 : 0;
    sections.splice(insertIndex, 0, unassigned);
  }

  // Filter out empty sections
  return sections.filter((section) => section.length > 0);
};

export const XptSecondaryNavBar: React.FC<any> = () => {
  const navigate = useNavigate();

  const { currentUserAccessLoadingStatus, currentUserAccessDetails } = useSelector((state: RootState) => state.xptAccessAndAuthorizationStore);

  const businessGroupsLoading = useSelector((state: RootState) => state.businessGroupStore.businessGroupLoadingStatus);
  const activeBusinessGroups = useSelector(selectActiveBusinessGroups);

  const [dataClassifications, setDataClassifications] = useState<DataClassification[]>([]);
  const dataClassificationShortDescShortDesc = useDataClassificationShortDescShortDescFromPath();

  useEffect(() => {
    if (currentUserAccessLoadingStatus === Status.Completed && businessGroupsLoading === Status.Completed) {
      const dataClassificationsThatAreAuthorizedToUser = currentUserAccessDetails?.user_data_classifications.map((dt) => dt.data_classification_id);
      const authorizedDataClassifications = activeBusinessGroups
        .filter((bg) => dataClassificationsThatAreAuthorizedToUser?.includes(bg.data_classification.data_classification_id!))
        .sort((a, b) => compareNullableNumbers(a.display_order_id ?? null, b.display_order_id ?? null, 'asc'))
        .map((bg) => bg.data_classification);
      setDataClassifications(authorizedDataClassifications);
    }
  }, [currentUserAccessLoadingStatus, businessGroupsLoading]);

  const onClickOfDataClassification = (dataClassification: DataClassification) => {
    logUserClick(
      `Selected ${dataClassification.data_classification_name} (${dataClassification.data_classification_short_description}) in Navigation`,
      'Top Navigation',
      'Landing Page'
    );
    navigateWithEmbedParams(navigate, '/' + dataClassification.data_classification_short_description.toLowerCase() + '/forecast-input');
  };

  const renderClassificationItem = (dataClassification: DataClassification, index: number) => {
    const isHighlighted =
      dataClassificationShortDescShortDesc?.toLowerCase() === dataClassification.data_classification_short_description?.toLowerCase();
    return (
      <div
        className={`global-secondary-nav-item ${isHighlighted ? 'highlighted-nav-item' : ''}`}
        key={`${dataClassification.data_classification_id}-${index}`}
        onClick={() => onClickOfDataClassification(dataClassification)}
      >
        <span className="display-name">{dataClassification.data_classification_name}</span>
      </div>
    );
  };

  // Group classifications into sections for rendering with dividers (memoized to avoid re-computation on every render)
  const sections = useMemo(() => groupClassificationsIntoSections(dataClassifications), [dataClassifications]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow, sections]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'right' ? 200 : -200, behavior: 'smooth' });
  };

  return (
    <ContentLayout className="app-secondary-top-navigation">
      <div className="secondary-nav-scroll-wrapper">
        {canScrollLeft && (
          <button className="scroll-arrow scroll-arrow-left" onClick={() => scroll('left')} aria-label="Scroll navigation left">
            <Icon name="angle-left" />
          </button>
        )}
        <div className="nav-scroll-container" ref={scrollContainerRef} onScroll={checkOverflow}>
          <SpaceBetween direction="horizontal" size="xs" className="nav-items-container">
            {sections.map((section, sectionIndex) => (
              <React.Fragment key={`section-${sectionIndex}`}>
                {sectionIndex > 0 && <div className="vertical-divider" />}
                {section.map((dataClassification, itemIndex) => renderClassificationItem(dataClassification, sectionIndex * 100 + itemIndex))}
              </React.Fragment>
            ))}
          </SpaceBetween>
        </div>
        {canScrollRight && (
          <button className="scroll-arrow scroll-arrow-right" onClick={() => scroll('right')} aria-label="Scroll navigation right">
            <Icon name="angle-right" />
          </button>
        )}
      </div>
    </ContentLayout>
  );
};
